import type { Component } from 'vue'
import type { RouteLocationNormalized, RouteLocationRaw } from 'vue-router'

import { notFoundLocation, type Address } from '@/router/not-found'
import type { Permission, SessionStage } from '@/stores/auth'

/**
 * Who may open a route (MRS 3.1, 6.1):
 * `link` is a page behind an emailed token, open to everyone;
 * `guest` is a page for a visitor who is not signed in;
 * `mfa-setup` and `mfa-challenge` are the two steps of a half-finished sign-in;
 * `staff` and `portal` are the two applications;
 * `any` is every other page, such as the not-found view and the public help centre.
 */
export type RouteAccess =
  'link' | 'guest' | 'mfa-setup' | 'mfa-challenge' | 'staff' | 'portal' | 'any'

/** A route with this is an entry of the navigation of its application. */
export interface RouteNav {
  /** The Lucide icon of the link. */
  readonly icon: Component
  /** The place in the list, lowest first. */
  readonly order: number
}

declare module 'vue-router' {
  interface RouteMeta {
    access: RouteAccess
    /** The permission that opens the route (MRS 3.2). Without it the kind of user is enough. */
    permission?: Permission
    /** The locale key of the page title: the document title and the navigation label. */
    titleKey?: string
    nav?: RouteNav
    /** Addresses with the same name share one view: it stays when the address changes. */
    view?: string
  }
}

export const SIGN_IN_ROUTE = 'auth.sign-in'
export const MFA_SETUP_ROUTE = 'auth.mfa.setup'
export const MFA_CHALLENGE_ROUTE = 'auth.mfa.challenge'
export const STAFF_HOME_ROUTE = 'staff.home'
export const PORTAL_HOME_ROUTE = 'portal.home'
export const PUBLIC_KB_ROUTE = 'public.kb'

/** What the guard reads of the auth store. */
export interface GuardSession {
  readonly stage: SessionStage
  readonly isStaff: boolean
  can: (permission: Permission) => boolean
}

/** What the guard reads of the page a user asks for. */
export type GuardTarget = Address & {
  readonly meta: { access: RouteAccess; permission?: Permission }
}

export function homeRoute(session: Pick<GuardSession, 'isStaff'>): string {
  return session.isStaff ? STAFF_HOME_ROUTE : PORTAL_HOME_ROUTE
}

type Verdict = true | RouteLocationRaw

function to(name: string): RouteLocationRaw {
  return { name }
}

/** A visitor opens the guest pages and the pages of no application; every other page asks for sign-in. */
function forVisitor(access: RouteAccess): Verdict {
  return access === 'guest' || access === 'any' ? true : to(SIGN_IN_ROUTE)
}

/**
 * A half-finished sign-in has one place to be. Every other route goes there,
 * so a staff user without a confirmed MFA secret reaches the set-up screen
 * and nothing else.
 */
function forHalfSignedIn(access: RouteAccess, step: 'mfa-setup' | 'mfa-challenge'): Verdict {
  if (access === step) return true
  return to(step === 'mfa-setup' ? MFA_SETUP_ROUTE : MFA_CHALLENGE_ROUTE)
}

/** A route with no permission is open to every user of its application. */
function holdsPermission(target: GuardTarget, session: GuardSession): boolean {
  return target.meta.permission === undefined || session.can(target.meta.permission)
}

/**
 * A page of the user's own application opens when the user holds its
 * permission. A page that the user may not see reads as a page that does not
 * exist (MRS 3.1), and the address stays in the bar.
 */
function forOwnApplication(target: GuardTarget, session: GuardSession): Verdict {
  return holdsPermission(target, session) ? true : notFoundLocation(target)
}

/** A staff user has no portal: `/portal` and every sign-in page send them to `/`. */
function forStaff(target: GuardTarget, session: GuardSession): Verdict {
  return target.meta.access === 'staff' ? forOwnApplication(target, session) : to(STAFF_HOME_ROUTE)
}

/** A customer user never learns that a staff page exists: it is the not-found view. */
function forCustomer(target: GuardTarget, session: GuardSession): Verdict {
  if (target.meta.access === 'portal') return forOwnApplication(target, session)
  return target.meta.access === 'staff' ? notFoundLocation(target) : to(PORTAL_HOME_ROUTE)
}

function forSignedIn(target: GuardTarget, session: GuardSession): Verdict {
  if (target.meta.access === 'any') return true
  return session.isStaff ? forStaff(target, session) : forCustomer(target, session)
}

/**
 * The decision for one navigation: `true` to go on, or the route to open in
 * its place. A page behind an emailed token is always open, because the token
 * starts a session of its own.
 */
export function decide(target: GuardTarget, session: GuardSession): Verdict {
  const access = target.meta.access
  if (access === 'link') return true
  if (session.stage === 'none') return forVisitor(access)
  if (session.stage === 'ready') return forSignedIn(target, session)
  return forHalfSignedIn(access, session.stage)
}

export interface GuardStore extends GuardSession {
  ensureLoaded: () => Promise<void>
}

/** The navigation guard. It reads the session once, before the first page. */
export function createAuthGuard(store: () => GuardStore) {
  return async (destination: RouteLocationNormalized): Promise<Verdict> => {
    const auth = store()
    await auth.ensureLoaded()
    return decide(destination, auth)
  }
}
